import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {ComuneModel, IndirizzoModel, LocalitaModel, NazioneModel} from '../../models/estremi-persona-fisica.model';
import {getErrorMessage} from '../../utils/validators-messages.utils';
import {ModalitaPagamentoService} from '../../services/modalita-pagamento.service';
import {VagliaBDIModel} from '../../models/vaglia-bdi.model';
import {ValidateName, ValidatePhone} from '../../utils/validators.utils';

@Component({
	selector: 'lib-vaglia-bdi',
	templateUrl: './vaglia-bdi.component.html',
	styleUrls: ['./vaglia-bdi.component.scss'],
})
export class VagliaBdiComponent implements OnInit, OnDestroy {

	@Input()
  vagliaBDI: VagliaBDIModel;

	@Input()
	comuni: ComuneModel[];

	@Input()
	capOptions: string[];

	@Input()
	isAntistatario: boolean;

	@Input()
	nazioni: NazioneModel[];

	@Input()
	province: LocalitaModel[];

	@Input()
	vagliaFormPath: string;

	@Input()
	indirizzoVagliaFormPath: string;

	@Output()
	setComuni = new EventEmitter<ComuneModel[]>();

	@Output()
	getCapOptions = new EventEmitter<string>();

	@Output()
	resetIndirizzoForm = new EventEmitter<void>();

	@Output()
	vagliaReady = new EventEmitter<{ vaglia: VagliaBDIModel, isValid: boolean }>();

	@Output()
	indirizzoEmitter = new EventEmitter<{ indirizzo: IndirizzoModel, isValid: boolean }>();

	cap: string[];

	vagliaForm: FormGroup;

	codiceCatastale: string;

	private subscription: Subscription = new Subscription();

	getErrorMessage = getErrorMessage;

	indirizzo: IndirizzoModel;
	isIndirizzoValid: boolean;

	constructor(private modalitaPagamentoService: ModalitaPagamentoService) {
		// this.initVagliaForm();
	}

	ngOnInit(): void {
		this.initVagliaForm();
		this.vagliaForm.setValidators(form => {
			return (!form.get('pec').value && !form.get('email').value) &&
					(form.get('domicilioRichiedenteCoincideConResidenza').value === false) ?
			{requiredMail: true} : null;
		});

		this.subscription.add(this.vagliaForm.controls['domicilioRichiedenteCoincideConResidenza'].valueChanges.subscribe(isDomicilio => {
			if(isDomicilio){
				this.vagliaForm.controls['nome'].setValidators([]);
				this.vagliaForm.controls['nome'].patchValue(null);
				this.vagliaForm.controls['nome'].updateValueAndValidity();
				this.vagliaForm.controls['cognome'].setValidators([]);
				this.vagliaForm.controls['cognome'].patchValue(null);
				this.vagliaForm.controls['cognome'].updateValueAndValidity();
				this.vagliaForm.controls["email"].patchValue(null);
				this.vagliaForm.controls["pec"].patchValue(null);
				this.vagliaForm.controls["telefono"].patchValue(null);
				this.resetIndirizzoForm.emit();
			}else{
				this.vagliaForm.controls['nome'].setValidators([Validators.required, ValidateName]),
				this.vagliaForm.controls['nome'].updateValueAndValidity();
				this.vagliaForm.controls['cognome'].setValidators([Validators.required, ValidateName]),
				this.vagliaForm.controls['cognome'].updateValueAndValidity();
			}
		}));

		this.vagliaForm.valueChanges.subscribe(() => {
      // if (this.vagliaForm.valid) {
        const vaglia: VagliaBDIModel = {
					intestatarioStudioLegale: this.vagliaForm.get('intestatoSudioLegale').value,
					domicilioRichiedenteCoincideConResidenza: this.vagliaForm.get('domicilioRichiedenteCoincideConResidenza').value,
					nome: this.vagliaForm.get('nome').value,
					denominazione:  this.vagliaForm.get('cognome').value,
					contatti: this.vagliaForm.get('domicilioRichiedenteCoincideConResidenza').value === true ? null :
							{
								email: this.vagliaForm.get('email').value,
								pec: this.vagliaForm.get('pec').value,
								telefono: this.vagliaForm.get('telefono').value,
							},
					indirizzo: this.vagliaForm.get('domicilioRichiedenteCoincideConResidenza').value === true ? null : this.indirizzo
				};

				const isFormValid = this.vagliaForm.valid;
				const isFormIndirizzoValid = this.vagliaForm.get('domicilioRichiedenteCoincideConResidenza').value === true ? true : this.isIndirizzoValid;
        const isValid = isFormValid && isFormIndirizzoValid;

        this.vagliaReady.emit({ vaglia: vaglia, isValid });
       //}
    });

	}

	initVagliaForm(): void {
		this.vagliaForm = new FormGroup({
			nome: new FormControl(this.vagliaBDI?.nome),
			cognome: new FormControl(this.vagliaBDI?.denominazione),
			email: new FormControl(this.vagliaBDI?.contatti?.email, [this.ValidateMail, Validators.maxLength(200)]),
			pec: new FormControl(this.vagliaBDI?.contatti?.pec, [this.ValidateMail, Validators.maxLength(200)]),
			telefono: new FormControl(this.vagliaBDI?.contatti?.telefono, [ValidatePhone]),
			intestatoSudioLegale: new FormControl(this.vagliaBDI != null ? this.vagliaBDI.intestatarioStudioLegale : false, this.isAntistatario ? [Validators.required] : null),
			domicilioRichiedenteCoincideConResidenza: new FormControl(this.vagliaBDI != null ? this.vagliaBDI.domicilioRichiedenteCoincideConResidenza : false, [Validators.required]),
		});
	}

	getComuni(siglaProvincia: string): void {
		if (siglaProvincia) {
			this.modalitaPagamentoService.getComuni$(siglaProvincia).pipe(take(1))
				.subscribe(comuni => {
						this.setComuni.emit(comuni);
					const comune = comuni.find(comuneItem => comuneItem.codiceCatastale === this.codiceCatastale);
					this.getCapOptions.emit(comune?.codiceIstat);
				})
		} else {
			this.setComuni.emit([]);
		}
	}

	getCap(codiceCatastale: string): void {
		if (codiceCatastale) {
			this.codiceCatastale = codiceCatastale;
		}
		const comune = this.comuni?.find(comuneItem => comuneItem.codiceCatastale === codiceCatastale);
		this.getCapOptions.emit(comune?.codiceIstat);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	ValidateMail(control: AbstractControl) {
		if (!control.value) {
			return null;
		}
		const formatoEmail: RegExp = /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/;
		if (formatoEmail.test(control.value)) {
			return null;
		}
		return { invalidMail: true };
	}

	onIndirizzoReady(event: { indirizzo: IndirizzoModel, isValid: boolean }) {
		this.indirizzo = event.indirizzo;
		this.isIndirizzoValid = event.isValid;
		this.indirizzoEmitter.emit({ indirizzo: event.indirizzo, isValid: event.isValid });
  }
}
